import { Icon } from "../ui/Icon";
import { NotificationItem } from "./NotificationItem";
import { useNotificationStore } from "../../stores/notification-store";
import {
  useNotifications,
  useMarkAllNotificationsRead,
} from "../../hooks/useNotifications";
import { GradientSpinner } from "../common/GradientSpinner";
import BellLinear from "~icons/solar/bell-linear";

interface NotificationDropdownProps {
  onClose: () => void;
}

export function NotificationDropdown({ onClose }: NotificationDropdownProps) {
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const recentNotifications = useNotificationStore(
    (s) => s.recentNotifications
  );

  const { isLoading } = useNotifications({ limit: 20 });
  const { mutate: markAllRead, isPending: isMarkingAll } =
    useMarkAllNotificationsRead();

  return (
    <div
      className="absolute right-0 top-full z-50 mt-2 w-80 rounded-lg border border-border-default bg-white shadow-lg sm:w-96"
      role="dialog"
      aria-label="Notifications"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border-light px-4 py-3">
        <h2 className="text-body-md font-semibold text-text-primary">
          Notifications
        </h2>
        {unreadCount > 0 && (
          <button
            type="button"
            className="text-body-sm text-primary transition-opacity hover:opacity-70 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            onClick={() => markAllRead()}
            disabled={isMarkingAll}
          >
            Mark all read
          </button>
        )}
      </div>

      {/* Body */}
      <div className="max-h-[400px] overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <GradientSpinner size={24} />
            <span className="sr-only">Loading notifications</span>
          </div>
        ) : recentNotifications.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <Icon
              icon={BellLinear}
              size="xl"
              className="text-text-placeholder"
              decorative={false}
              aria-label="No notifications"
            />
            <p className="text-body-sm text-text-placeholder">
              No notifications yet
            </p>
          </div>
        ) : (
          <ul role="list">
            {recentNotifications.map((notification) => (
              <li
                key={notification.id}
                className="border-b border-border-light last:border-b-0"
              >
                <NotificationItem
                  notification={notification}
                  onClose={onClose}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
